import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Ban,
  CheckCircle2,
  Copy,
  Download,
  FileText,
  Link2,
  MessageCircle,
  Pencil,
  RefreshCw,
  Send,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatCurrency, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { QUOTE_STATUS_BADGE } from "@/pages/Quotes";

function ThreadBubble({ msg }) {
  const isCustomer = msg.author === "customer";
  return (
    <div
      className={`flex ${isCustomer ? "justify-start" : "justify-end"}`}
      data-testid={`qd-msg-${msg.author}`}
    >
      <div
        className={[
          "max-w-[80%] rounded-2xl px-3 py-2 text-sm",
          isCustomer
            ? "border border-slate-200 bg-white text-slate-800"
            : "bg-indigo-600 text-white",
        ].join(" ")}
      >
        <div className={`mb-0.5 text-[10px] font-semibold uppercase tracking-wider ${isCustomer ? "text-indigo-600" : "text-indigo-100"}`}>
          {isCustomer ? "Customer" : (msg.author_name || "You")}
        </div>
        <div className="whitespace-pre-wrap">{msg.text}</div>
        <div className={`mt-1 text-[10px] ${isCustomer ? "text-slate-400" : "text-indigo-200"}`}>
          {new Date(msg.at).toLocaleString()}
        </div>
      </div>
    </div>
  );
}

function QuoteThreadReadOnly({ messages }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="quote-detail-thread-readonly">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700">
        <MessageCircle className="h-4 w-4 text-indigo-600" /> Conversation
      </div>
      <div className="space-y-2">
        {messages.map((m, i) => <ThreadBubble key={i} msg={m} />)}
      </div>
    </div>
  );
}

function QuoteReplyPanel({ quote, messages, onSent }) {
  const { t } = useTranslation();
  const [message, setMessage] = useState("");
  const [regen, setRegen] = useState(false);
  const [extendDays, setExtendDays] = useState(30);
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!message.trim()) return;
    setBusy(true);
    try {
      const payload = { message: message.trim(), regenerate_link: regen };
      if (regen) payload.extend_days = Number(extendDays) || 30;
      await api.post(`/quotes/${quote.id}/reply`, payload);
      toast.success(t("quoteReply.sent"));
      setMessage("");
      setRegen(false);
      await onSent();
    } catch (err) {
      toast.error(formatApiError(err, t("quotes.toasts.error")));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-4 shadow-sm" data-testid="quote-detail-reply-panel">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-indigo-900">
        <MessageCircle className="h-4 w-4" /> {t("quoteReply.threadTitle")}
      </div>
      {messages.length === 0 ? (
        <div className="text-sm text-slate-500">{t("quoteReply.empty")}</div>
      ) : (
        <div className="space-y-2">
          {messages.map((m, i) => <ThreadBubble key={i} msg={m} />)}
        </div>
      )}
      <div className="mt-4 space-y-2">
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={t("quoteReply.replyPlaceholder")}
          rows={3}
          data-testid="quote-detail-reply-input"
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:border-indigo-400 focus:outline-none"
        />
        <label className="flex items-center gap-2 text-xs text-slate-700">
          <input
            type="checkbox"
            checked={regen}
            onChange={(e) => setRegen(e.target.checked)}
            data-testid="quote-detail-reply-regen"
            className="h-4 w-4 accent-indigo-600"
          />
          {t("quoteReply.regenerateLink")}
        </label>
        {regen && (
          <div className="flex items-center gap-2 text-xs text-slate-700">
            <label>{t("quoteReply.extendDays")}</label>
            <input
              type="number"
              min="1"
              max="365"
              value={extendDays}
              onChange={(e) => setExtendDays(e.target.value)}
              data-testid="quote-detail-reply-extend"
              className="w-24 rounded border border-slate-300 bg-white px-2 py-1"
            />
          </div>
        )}
        <div className="flex justify-end">
          <Button
            onClick={submit}
            disabled={busy || !message.trim()}
            data-testid="quote-detail-reply-send"
            className="gap-1 bg-indigo-600 text-white hover:bg-indigo-700"
          >
            <Send className="h-4 w-4" /> {t("quoteReply.sendReply")}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function QuoteDetail() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, company } = useAuth();
  const lang = i18n.language?.split("-")[0] || "en";

  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/quotes/${id}`);
      setQuote(data);
    } catch (err) {
      toast.error(formatApiError(err, t("quotes.toasts.error")));
      navigate("/app/quotes", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [id, navigate, t]);

  useEffect(() => { load(); }, [load]);

  async function action(fn, label) {
    setBusy(true);
    try {
      await fn();
      toast.success(label);
      await load();
    } catch (err) {
      toast.error(formatApiError(err, t("quotes.toasts.error")));
    } finally {
      setBusy(false);
    }
  }

  function copyPublicLink() {
    if (!quote?.public_token) return;
    const url = `${window.location.origin}/p/quote/${quote.public_token}`;
    navigator.clipboard.writeText(url);
    toast.success(t("quotes.toasts.linkCopied"));
  }

  async function downloadPdf(pdfLang) {
    try {
      const token = localStorage.getItem("invoicepro.token");
      const res = await fetch(`${API_BASE}/quotes/${id}/pdf?lang=${pdfLang}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("PDF failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${quote.quote_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t("quotes.toasts.pdfError"));
    }
  }

  async function convert() {
    setBusy(true);
    try {
      const { data } = await api.post(`/quotes/${id}/convert-to-invoice`);
      toast.success(t("quotes.toasts.converted"));
      navigate(`/app/invoices/${data.invoice.id}`);
    } catch (err) {
      toast.error(formatApiError(err, t("quotes.toasts.error")));
    } finally {
      setBusy(false);
    }
  }

  if (loading || !quote) return <div className="p-6 text-slate-500" data-testid="quote-detail-loading">{t("common.loading")}</div>;

  const isDraft = quote.status === "draft";
  const isAccepted = quote.status === "accepted";
  const canDelete = user?.role === "admin";
  const canEdit = user?.role === "admin" || user?.role === "accountant";

  return (
    <div className="max-w-4xl space-y-6" data-testid="quote-detail-page">
      <Link to="/app/quotes" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" /> {t("quotes.backToList")}
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-4xl tracking-tight text-slate-900" data-testid="quote-detail-number">
              {quote.quote_number}
            </h1>
            <Badge className={QUOTE_STATUS_BADGE[quote.status] || QUOTE_STATUS_BADGE.draft} data-testid="quote-detail-status">
              {t(`quotes.status.${quote.status}`)}
            </Badge>
          </div>
          <div className="mt-2 text-sm text-slate-500">
            {t("quotes.validUntil")}: <strong className="text-slate-800">{formatDate(quote.expires_at, lang)}</strong>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {isDraft && canEdit && (
            <Button variant="outline" onClick={() => navigate(`/app/quotes/${id}/edit`)} data-testid="quote-detail-edit" className="gap-1">
              <Pencil className="h-4 w-4" /> {t("common.edit")}
            </Button>
          )}
          {(isDraft || quote.status === "sent" || quote.status === "expired") && canEdit && (
            <Button onClick={() => action(() => api.post(`/quotes/${id}/send`), t("quotes.toasts.sent"))} disabled={busy} data-testid="quote-detail-send" className="gap-1 bg-indigo-600 hover:bg-indigo-700 text-white">
              <Send className="h-4 w-4" /> {t("quotes.actions.send")}
            </Button>
          )}
          {quote.public_token && (
            <Button variant="outline" onClick={copyPublicLink} data-testid="quote-detail-copy-link" className="gap-1">
              <Copy className="h-4 w-4" /> {t("quotes.actions.copyLink")}
            </Button>
          )}
          <Button variant="outline" onClick={() => downloadPdf(lang)} data-testid="quote-detail-pdf" className="gap-1">
            <Download className="h-4 w-4" /> {t("quotes.actions.downloadPdf")}
          </Button>
          {(quote.status === "sent" || quote.status === "expired") && canEdit && (
            <Button variant="outline" onClick={() => action(() => api.post(`/quotes/${id}/regenerate-public-link`), t("quotes.toasts.linkRegenerated"))} disabled={busy} data-testid="quote-detail-regen" className="gap-1">
              <RefreshCw className="h-4 w-4" /> {t("quotes.actions.regenLink")}
            </Button>
          )}
          {isAccepted && canEdit && (
            <Button onClick={convert} disabled={busy} data-testid="quote-detail-convert" className="gap-1 bg-emerald-600 hover:bg-emerald-700 text-white">
              <FileText className="h-4 w-4" /> {t("quotes.actions.convert")}
            </Button>
          )}
          {!["cancelled", "converted"].includes(quote.status) && canEdit && (
            <Button variant="outline" onClick={() => action(() => api.post(`/quotes/${id}/cancel`), t("quotes.toasts.cancelled"))} disabled={busy} data-testid="quote-detail-cancel" className="gap-1 text-slate-700">
              <Ban className="h-4 w-4" /> {t("quotes.actions.cancel")}
            </Button>
          )}
          {isDraft && canDelete && (
            <Button variant="ghost" onClick={() => action(() => api.delete(`/quotes/${id}`), t("quotes.toasts.deleted")).then(() => navigate("/app/quotes"))} disabled={busy} data-testid="quote-detail-delete" className="gap-1 text-red-600 hover:text-red-700">
              <Trash2 className="h-4 w-4" /> {t("common.delete")}
            </Button>
          )}
        </div>
      </header>

      {quote.customer_response && (
        <div
          className={`rounded-xl border p-4 text-sm ${
            quote.customer_response.status === "accepted"
              ? "border-emerald-200 bg-emerald-50 text-emerald-900"
              : quote.customer_response.status === "rejected"
              ? "border-red-200 bg-red-50 text-red-900"
              : "border-amber-200 bg-amber-50 text-amber-900"
          }`}
          data-testid="quote-detail-response"
        >
          <div className="flex items-center gap-2 font-semibold">
            <CheckCircle2 className="h-4 w-4" />
            {t(`quotes.response.${quote.customer_response.status}`, { defaultValue: quote.customer_response.status })}
            <span className="ms-auto text-xs opacity-70">
              {new Date(quote.customer_response.last_activity_at || quote.customer_response.responded_at).toLocaleString()}
            </span>
          </div>
          {quote.customer_response.note && (
            <div className="mt-2 whitespace-pre-wrap text-sm">
              {quote.customer_response.note}
            </div>
          )}
        </div>
      )}

      {quote.customer_response && quote.customer_response.status === "thinking" && canEdit && (
        <QuoteReplyPanel
          quote={quote}
          messages={quote.customer_response.messages || []}
          onSent={load}
        />
      )}

      {quote.customer_response && (quote.customer_response.messages || []).length > 0 &&
       quote.customer_response.status !== "thinking" && (
        <QuoteThreadReadOnly messages={quote.customer_response.messages || []} />
      )}

      {quote.converted_invoice_id && (
        <div className="rounded-xl border border-violet-200 bg-violet-50 p-3 text-sm text-violet-900" data-testid="quote-detail-converted-notice">
          {t("quotes.convertedToInvoice")}{" "}
          <Link to={`/app/invoices/${quote.converted_invoice_id}`} className="font-semibold underline">
            {t("quotes.viewInvoice")}
          </Link>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500">{t("quotes.customer")}</div>
          <div className="mt-1 text-lg font-semibold text-slate-900">{quote.customer?.name}</div>
          <div className="text-sm text-slate-600">{quote.customer?.email}</div>
          {quote.customer?.address && (
            <div className="mt-2 text-xs text-slate-500">
              {quote.customer.address.street}, {quote.customer.address.postal_code} {quote.customer.address.city}
            </div>
          )}
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500">{t("quotes.summary")}</div>
          <dl className="mt-2 space-y-1 text-sm">
            <div className="flex justify-between"><dt className="text-slate-500">{t("quotes.issueDate")}</dt><dd>{formatDate(quote.issue_date, lang)}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">{t("quotes.validUntil")}</dt><dd className="font-semibold">{formatDate(quote.expires_at, lang)}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">{t("quotes.currency")}</dt><dd>{quote.currency}</dd></div>
            <div className="flex justify-between border-t border-slate-100 pt-2"><dt className="text-slate-700 font-semibold">{t("quotes.total")}</dt><dd className="font-semibold tabular-nums" data-testid="quote-detail-total">{formatCurrency(quote.total, quote.currency, lang)}</dd></div>
          </dl>
        </div>
      </div>

      <div className="mobile-cards overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3 text-start">{t("quotes.name")}</th>
              <th className="px-4 py-3 text-end">{t("quotes.qty")}</th>
              <th className="px-4 py-3 text-end">{t("quotes.unitPrice")}</th>
              <th className="px-4 py-3 text-end">{t("quotes.taxPct")}</th>
              <th className="px-4 py-3 text-end">{t("quotes.lineTotal")}</th>
            </tr>
          </thead>
          <tbody>
            {quote.line_items.map((li, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="px-4 py-3" data-label={t("quotes.name")}>
                  <div className="font-medium text-slate-900">{li.name}</div>
                  {li.description && <div className="text-xs text-slate-500">{li.description}</div>}
                </td>
                <td className="px-4 py-3 text-end tabular-nums" data-label={t("quotes.qty")}>{li.quantity} {li.unit || ""}</td>
                <td className="px-4 py-3 text-end tabular-nums" data-label={t("quotes.unitPrice")}>{formatCurrency(li.unit_price, quote.currency, lang)}</td>
                <td className="px-4 py-3 text-end tabular-nums" data-label={t("quotes.taxPct")}>{li.tax_rate}%</td>
                <td className="px-4 py-3 text-end font-medium tabular-nums" data-label={t("quotes.lineTotal")}>{formatCurrency(li.total, quote.currency, lang)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {quote.notes && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">{t("quotes.notes")}</div>
          <div className="whitespace-pre-wrap text-sm text-slate-700">{quote.notes}</div>
        </div>
      )}
    </div>
  );
}
